/**
 * AI API 路由
 */

import { Router } from 'express';
import { AIService } from '../services/ai-service.js';
import { RequestWithProject } from '../server.js';

export function createAIRouter(): Router {
  const router = Router();
  let aiService: AIService;
  
  // 中间件：确保 AI 服务已初始化
  router.use((req: RequestWithProject, res, next) => {
    if (!aiService) {
      // 从项目配置初始化 AI 服务
      const options = {
        enableMemory: process.env.WORKFLOW_ENABLE_MEMORY === 'true',
        userId: req.headers['x-user-id'] as string || process.env.USER || 'anonymous',
        projectId: req.projectInfo?.id,
      };
      aiService = new AIService(options);
    }
    next();
  });
  
  // GET /api/v2/ai/status
  router.get('/status', async (req, res) => {
    try {
      const status = await aiService.getAIStatus();
      res.json(status);
    } catch (error) {
      res.status(500).json({ error: error.message });
    }
  });
  
  // POST /api/v2/ai/recommend-agent
  router.post('/recommend-agent', async (req: RequestWithProject, res) => {
    try {
      const { taskId } = req.body;
      if (!taskId) {
        return res.status(400).json({ error: 'taskId is required' });
      }
      
      const recommendation = await aiService.recommendAgent(taskId);
      res.json(recommendation);
    } catch (error) {
      res.status(500).json({ error: error.message });
    }
  });
  
  // POST /api/v2/ai/recommend-agents
  router.post('/recommend-agents', async (req: RequestWithProject, res) => {
    try {
      const { taskIds } = req.body;
      if (!taskIds || !Array.isArray(taskIds)) {
        return res.status(400).json({ error: 'taskIds array is required' });
      }
      
      const recommendations = await aiService.recommendAgentsForTasks(taskIds);
      res.json({ recommendations });
    } catch (error) {
      res.status(500).json({ error: error.message });
    }
  });
  
  // POST /api/v2/ai/task-suggestions
  router.post('/task-suggestions', async (req: RequestWithProject, res) => {
    try {
      const { description } = req.body;
      if (!description) {
        return res.status(400).json({ error: 'description is required' });
      }
      
      const suggestions = await aiService.getTaskSuggestions(description);
      res.json(suggestions);
    } catch (error) {
      res.status(500).json({ error: error.message });
    }
  });
  
  // POST /api/v2/ai/record-performance
  router.post('/record-performance', async (req: RequestWithProject, res) => {
    try {
      const { agentName, taskId, outcome } = req.body;
      
      if (!agentName || !taskId || !outcome) {
        return res.status(400).json({ 
          error: 'agentName, taskId, and outcome are required' 
        });
      }
      
      if (!['success', 'failure', 'reassigned'].includes(outcome)) {
        return res.status(400).json({ 
          error: 'outcome must be one of: success, failure, reassigned' 
        });
      }
      
      await aiService.recordAgentPerformance(agentName, taskId, outcome);
      res.json({ success: true });
    } catch (error) {
      res.status(500).json({ error: error.message });
    }
  });
  
  // GET /api/v2/ai/agent-stats
  router.get('/agent-stats', async (req, res) => {
    try {
      const stats = await aiService.getAgentStats();
      res.json(stats);
    } catch (error) {
      res.status(500).json({ error: error.message });
    }
  });
  
  return router;
}